import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
} from '@mui/material'
import { modificarSellIn, getUsersToConfig } from 'src/services/motosSnopServices'

const ModificarPlanVentasModal = ({ 
  open, 
  onClose, 
  mesesColumnas, 
  modeloSeleccionado,
  onSaveSuccess,
  tipoPeriodo = 'mes',
  escenario = 'inicial_2025_6'
}) => {
  const [descripcion, setDescripcion] = useState('')
  const [responsable, setResponsable] = useState('')
  const [datosVentas, setDatosVentas] = useState({})
  const [usuarios, setUsuarios] = useState([])
  const [loadingUsuarios, setLoadingUsuarios] = useState(false)

  const fetchUsuarios = async () => {
    setLoadingUsuarios(true)
    try {
      const response = await getUsersToConfig()
      if (response && response.usuarios) {
        setUsuarios(response.usuarios)
      }
    } catch (error) {
      console.error('Error al cargar usuarios:', error)
    } finally {
      setLoadingUsuarios(false)
    }
  }

  // Cargar usuarios cuando se abre el modal
  useEffect(() => {
    if (open) {
      fetchUsuarios()
    }
  }, [open])

  // Función para obtener la semana actual del año
  const getWeekNumber = (date) => {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1)
    const pastDaysOfYear = (date - firstDayOfYear) / 86400000
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7)
  }

  // Convertir columnas a formato de fecha según el tipo de período
  const convertirColumnasAFecha = () => {
    const fechaActual = new Date()
    const añoActual = fechaActual.getFullYear()
    const mesActual = fechaActual.getMonth() + 1 // getMonth() retorna 0-11
    const semanaActual = getWeekNumber(fechaActual)

    if (tipoPeriodo === 'semana') {
      // Para semanas: formato "01-2026", "02-2026", etc.
      return mesesColumnas.map(semanaCol => {
        const [semana, año] = semanaCol.split('-')
        const semanaNumero = parseInt(semana, 10)
        const añoCompleto = parseInt(año, 10)
        
        return {
          periodo: semanaNumero,
          año: añoCompleto,
          display: semanaCol,
          key: `${añoCompleto}-${semanaNumero}`,
          tipo: 'semana'
        }
      }).filter(semanaObj => {
        // Filtrar solo semanas actuales y futuras
        if (semanaObj.año > añoActual) return true
        if (semanaObj.año === añoActual && semanaObj.periodo >= semanaActual) return true
        return false
      })
    } 
      // Para meses: formato "ene-26", "feb-26", etc.
      const nombresMeses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
      
      return mesesColumnas.map(mesCol => {
        const [nombreMes, año] = mesCol.split('-')
        const mesNumero = nombresMeses.indexOf(nombreMes) + 1
        const añoCompleto = 2000 + parseInt(año, 10)
        
        return {
          periodo: mesNumero,
          año: añoCompleto,
          display: mesCol,
          key: `${añoCompleto}-${mesNumero}`,
          tipo: 'mes'
        }
      }).filter(mesObj => {
        // Filtrar solo meses actuales y futuros
        if (mesObj.año > añoActual) return true
        if (mesObj.año === añoActual && mesObj.periodo >= mesActual) return true
        return false
      })
    
  }

  const periodosDisponibles = convertirColumnasAFecha()

  const handleInputChange = (mesKey, tipo, valor) => {
    // Solo permitir números
    if (!/^\d*$/.test(valor)) return
    
    setDatosVentas(prev => {
      const nuevoDato = { ...prev[mesKey] }
      
      // Si se está llenando un campo, limpiar el otro
      if (valor) {
        if (tipo === 'incremento') {
          nuevoDato.decremento = ''
        } else if (tipo === 'decremento') {
          nuevoDato.incremento = ''
        }
      }
      
      nuevoDato[tipo] = valor
      
      return {
        ...prev,
        [mesKey]: nuevoDato
      }
    })
  }

  const handleGuardar = async () => {
    const informacionSellIn = []
    
    Object.keys(datosVentas).forEach(periodoKey => {
      const datos = datosVentas[periodoKey]
      const [año, periodo] = periodoKey.split('-').map(Number)
      
      if (datos.incremento && parseInt(datos.incremento, 10) > 0) {
        const entry = {
          anio: año,
          sell_in: parseInt(datos.incremento, 10),
          operacion: 1
        }
        entry.mes = periodo
        informacionSellIn.push(entry)
      }
      
      if (datos.decremento && parseInt(datos.decremento, 10) > 0) {
        const entry = {
          anio: año,
          sell_in: parseInt(datos.decremento, 10),
          operacion: 0
        }
          entry.mes = periodo
        informacionSellIn.push(entry)
      }
    })

    const sellInData = {
      informacion: {
        modelo_planeacion: modeloSeleccionado,
        escenario,
        id_responsable: parseInt(responsable, 10),
        tipo_periodo: tipoPeriodo,
        descripcion,
      },
      informacion_sell_in: informacionSellIn
    }

    try {
      const response = await modificarSellIn(sellInData)
      if (response) {
        // Limpiar formulario
        setDescripcion('')
        setResponsable('')
        setDatosVentas({})
        
        // Cerrar modal
        onClose()
        
        // Llamar callback para recargar datos
        if (onSaveSuccess) {
          onSaveSuccess()
        }
      }
    } catch (error) {
      console.error('Error al guardar sell in:', error)
    }
  }

  const handleClose = () => {
    setDescripcion('')
    setResponsable('')
    setDatosVentas({})
    onClose()
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '600px',
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          backgroundColor: '#e8f5e8',
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: '1.2rem'
        }}
      >
        MODIFICACIÓN PLAN DE VENTAS
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
          Crear evento
        </Typography>

        {/* Campos del formulario */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Descripción"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              variant="outlined"
              sx={{ mb: 2 }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Responsable</InputLabel>
              <Select
                value={responsable}
                onChange={(e) => setResponsable(e.target.value)}
                label="Responsable"
                disabled={loadingUsuarios}
              >
                {loadingUsuarios ? (
                  <MenuItem disabled>
                    <em>Cargando usuarios...</em>
                  </MenuItem>
                ) : (
                  usuarios.map((usuario) => (
                    <MenuItem key={usuario.id} value={usuario.id}>
                      {`${usuario.nombre} ${usuario.apellido}`}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
          {tipoPeriodo === 'semana' ? 'Añadir semana y cantidad' : 'Añadir mes y cantidad'}
        </Typography>

        <Typography variant="body2" sx={{ mb: 2, fontWeight: 'bold' }}>
          Evento: Push Distribuidores
        </Typography>

        {/* Tabla de períodos */}
        <Paper elevation={1} sx={{ p: 2 }}>
          {periodosDisponibles.map((periodo) => (
            <Box key={periodo.key} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ width: 80, fontWeight: 'bold' }}>
                {periodo.display}
              </Box>
              
              <TextField
                size="small"
                placeholder="Incremento"
                value={datosVentas[periodo.key]?.incremento || ''}
                onChange={(e) => handleInputChange(periodo.key, 'incremento', e.target.value)}
                disabled={!!datosVentas[periodo.key]?.decremento}
                sx={{ 
                  mx: 1,
                  width: 120,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: datosVentas[periodo.key]?.incremento ? '#e8f5e8' : 
                                   datosVentas[periodo.key]?.decremento ? '#f5f5f5' : 'white',
                    '& fieldset': {
                      borderColor: datosVentas[periodo.key]?.incremento ? '#4caf50' : '#ccc',
                    },
                  },
                }}
                slotProps={{
                  htmlInput: {
                    pattern: '[0-9]*',
                    inputMode: 'numeric'
                  }
                }}
              />
              
              <TextField
                size="small"
                placeholder="Decremento"
                value={datosVentas[periodo.key]?.decremento || ''}
                onChange={(e) => handleInputChange(periodo.key, 'decremento', e.target.value)}
                disabled={!!datosVentas[periodo.key]?.incremento}
                sx={{ 
                  mx: 1,
                  width: 120,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: datosVentas[periodo.key]?.decremento ? '#ffebee' : 
                                   datosVentas[periodo.key]?.incremento ? '#f5f5f5' : 'white',
                    '& fieldset': {
                      borderColor: datosVentas[periodo.key]?.decremento ? '#f44336' : '#ccc',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: datosVentas[periodo.key]?.decremento ? '#f44336' : 'inherit',
                  }
                }}
                slotProps={{
                  htmlInput: {
                    pattern: '[0-9]*',
                    inputMode: 'numeric'
                  },
                  input: {
                    startAdornment: datosVentas[periodo.key]?.decremento ? '-' : null,
                  }
                }}
              />
            </Box>
          ))}
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3, justifyContent: 'center' }}>
        <Button
          onClick={handleGuardar}
          variant="contained"
          sx={{
            backgroundColor: '#d3d3d3',
            color: '#333',
            fontWeight: 'bold',
            px: 4,
            py: 1.5,
            '&:hover': {
              backgroundColor: '#bbb',
            },
          }}
          disabled={!descripcion || !responsable || loadingUsuarios}
        >
          GUARDAR CAMBIOS
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ModificarPlanVentasModal 